import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, Check } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import CountUp from "@/components/CountUp";
import { INDUSTRIES, SERVICES } from "@/data/evara";

const EASE = [0.4, 0, 0.2, 1];

function Reveal({ delay = 0, y = 20, children, className = "" }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: EASE, delay: prefersReducedMotion ? 0 : delay }}
    >
      {children}
    </motion.div>
  );
}

/* --------------- Industries HUB view --------------- */
function IndustriesHub({ onSelect }) {
  const prefersReducedMotion = useReducedMotion();
  const [hoverKey, setHoverKey] = React.useState(null);

  return (
    <>
      <section className="section-pad bg-sage-gradient" data-testid="industries-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Who we serve</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              Built for healthcare&apos;s{" "}
              <em className="not-italic text-[var(--evara-primary)] font-medium">full spectrum.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] max-w-3xl leading-relaxed">
              Pick a sector to see how EVARA adapts to your clinical, cultural and commercial reality.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sector tile grid */}
      <section className="section-pad pt-16" data-testid="industries-grid">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow mb-8">Explore a sector</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDUSTRIES.map((ind, i) => {
              const hovered = hoverKey === ind.key;
              return (
                <motion.button
                  key={ind.key}
                  type="button"
                  onClick={() => onSelect(ind.key)}
                  onMouseEnter={() => setHoverKey(ind.key)}
                  onMouseLeave={() => setHoverKey(null)}
                  onFocus={() => setHoverKey(ind.key)}
                  onBlur={() => setHoverKey(null)}
                  className={
                    "relative text-left rounded-2xl border bg-white p-6 md:p-7 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evara-primary)] flex flex-col overflow-hidden " +
                    (hovered
                      ? "border-[var(--evara-border-strong)]"
                      : "border-[var(--evara-border)] hover:border-[var(--evara-border-strong)]")
                  }
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
                  whileHover={prefersReducedMotion ? {} : { y: -4 }}
                  data-testid={`industry-tile-${ind.key}`}
                >
                  {ind.image && (
                    <div className="evara-image sector-tile-bg absolute inset-0 rounded-2xl pointer-events-none" aria-hidden="true">
                      <img src={ind.image} alt="" loading="lazy" />
                    </div>
                  )}
                  <div className="relative">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                      <Icon name={ind.icon} size={20} />
                    </span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl mt-5 text-[var(--evara-primary)] leading-tight">
                    {ind.name}
                  </h3>

                  {/* Hover preview: first "What We Understand About You" bullet */}
                  <div
                    className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed transition-all overflow-hidden"
                    style={{
                      maxHeight: hovered ? 120 : 0,
                      opacity: hovered ? 1 : 0,
                    }}
                    aria-hidden={!hovered}
                  >
                    <span className="italic">&ldquo;{ind.understand[0]}&rdquo;</span>
                  </div>

                  {/* Blurb (always visible when not hovered) */}
                  {!hovered && (
                    <p className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed">
                      {ind.blurb}
                    </p>
                  )}

                  <div className="mt-auto pt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)]">
                    Explore sector <ArrowRight size={14} />
                  </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-pad pt-6" data-testid="industries-cta">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="rounded-3xl border border-[var(--evara-border)] bg-[var(--evara-bg-soft)] p-8 md:p-10 flex flex-wrap items-center justify-between gap-6">
              <div>
                <div className="eyebrow">Not sure where you fit?</div>
                <div className="font-serif text-2xl md:text-3xl mt-2 text-[var(--evara-primary)] leading-tight">
                  Book a call and we&apos;ll map it to your context.
                </div>
              </div>
              <Link to="/book" className="btn-primary" data-testid="industries-book-btn">
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

/* --------------- Industries FOCUSED (single sector) view --------------- */
function SectorPage({ sector, index, onSelect }) {
  const prefersReducedMotion = useReducedMotion();
  const others = INDUSTRIES.filter((s) => s.key !== sector.key);
  const relevantServices = (sector.services || [])
    .map((k) => SERVICES.find((s) => s.key === k))
    .filter(Boolean);

  return (
    <>
      {/* 1. HERO */}
      <section className="section-pad bg-sage-gradient" data-testid="sector-hero">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Link
              to="/industries"
              className="inline-flex items-center gap-2 text-sm font-bold text-[var(--evara-primary)] hover:underline"
              data-testid="sector-view-all-link"
            >
              <ChevronLeft size={16} /> View all sectors
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-5 flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                <Icon name={sector.icon} size={20} />
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              <em className="not-italic text-[var(--evara-primary)] font-medium">{sector.name}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg text-[var(--evara-text-2)] leading-relaxed max-w-3xl">
              {sector.overview}
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2. WHAT WE UNDERSTAND ABOUT YOU */}
      <section className="section-pad pt-14" data-testid="sector-understand">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <div className="eyebrow">What we understand about you</div>
              <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight text-[var(--evara-primary)]">
                These are the pressures we start with, not generic ones.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <ul className="space-y-3">
              {sector.understand.map((line, i) => (
                <motion.li
                  key={line}
                  className="flex items-start gap-3 rounded-2xl border border-[var(--evara-border)] bg-white p-4 md:p-5"
                  initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, ease: EASE, delay: i * 0.08 }}
                  data-testid={`sector-understand-${sector.key}-${i}`}
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                    <Check size={14} />
                  </span>
                  <span className="text-sm md:text-base text-[var(--evara-text)] leading-relaxed pt-0.5">
                    {line}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 3. RELEVANT EVARA SERVICES */}
      <section className="section-pad pt-14" data-testid="sector-services">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl border border-[var(--evara-border)] bg-[var(--evara-bg-soft)] p-6 md:p-10">
            <Reveal>
              <div className="eyebrow">Relevant EVARA services</div>
              <h2 className="font-serif text-2xl md:text-3xl mt-3 text-[var(--evara-primary)] leading-tight">
                {relevantServices.length} of our 7 practices are tuned for{" "}
                <em className="not-italic">{sector.name}</em>.
              </h2>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {relevantServices.map((s, i) => (
                <motion.div
                  key={s.key}
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, ease: EASE, delay: i * 0.08 }}
                >
                  <Link
                    to={`/services?practice=${s.key}`}
                    className="group flex items-start gap-4 rounded-2xl border border-[var(--evara-border)] bg-white p-5 hover:border-[var(--evara-border-strong)] transition-colors"
                    data-testid={`sector-service-chip-${sector.key}-${s.key}`}
                  >
                    <span className="shrink-0 w-11 h-11 rounded-xl bg-[var(--evara-bg-mint)] text-[var(--evara-primary)] flex items-center justify-center">
                      <Icon name={s.icon} size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg text-[var(--evara-primary)] leading-tight">
                        {s.title}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-[var(--evara-primary)] group-hover:gap-2 transition-all">
                        Explore practice <ArrowRight size={12} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUTCOMES with count-up */}
      <section className="section-pad pt-14 bg-soft-sage" data-testid="sector-outcomes">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="eyebrow">Outcomes you can expect</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
              What we typically move,{" "}
              <em className="not-italic text-[var(--evara-primary)] font-medium">measurably.</em>
            </h2>
          </Reveal>

          {sector.stats && (
            <div className="grid sm:grid-cols-3 gap-5 mt-10">
              {sector.stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  className="rounded-2xl border border-[var(--evara-border)] bg-white p-6"
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, ease: EASE, delay: i * 0.08 }}
                  data-testid={`sector-stat-${sector.key}-${i}`}
                >
                  <CountUp
                    value={s.value}
                    prefix={s.prefix || ""}
                    suffix={s.suffix || ""}
                    className="font-serif text-4xl md:text-5xl text-[var(--evara-primary)] leading-none"
                  />
                  <div className="mt-4 h-[1px] w-10 bg-[var(--evara-mint)]" />
                  <div className="mt-3 text-sm text-[var(--evara-text-2)] leading-relaxed">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <ul className="mt-10 grid md:grid-cols-2 gap-y-3 gap-x-8">
            {sector.outcomes.map((o, i) => (
              <motion.li
                key={o}
                className="flex items-start gap-3 text-sm"
                initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, ease: EASE, delay: i * 0.05 }}
                data-testid={`sector-outcome-${sector.key}-${i}`}
              >
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[var(--evara-primary)] shrink-0" />
                <span className="leading-relaxed">{o}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5. SECTOR SWITCHER */}
      <section className="section-pad pt-14 pb-16" data-testid="sector-switcher">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl border border-[var(--evara-border)] bg-white p-6 md:p-8">
            <Reveal>
              <div className="eyebrow mb-4">Jump to another sector</div>
            </Reveal>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {/* Current sector — disabled visual */}
              <span
                className="inline-flex items-center gap-2 rounded-full bg-[var(--evara-primary)] text-white px-4 py-2 text-sm font-semibold cursor-default"
                aria-current="page"
                data-testid={`sector-switcher-current-${sector.key}`}
              >
                <Icon name={sector.icon} size={14} />
                {sector.name}
              </span>

              {others.map((o, i) => (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => onSelect(o.key)}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-[var(--evara-border)] text-[var(--evara-text)] px-4 py-2 text-sm font-semibold hover:border-[var(--evara-primary)] hover:text-[var(--evara-primary)] transition-colors"
                  initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.3, ease: EASE, delay: i * 0.05 }}
                  data-testid={`sector-switcher-${o.key}`}
                >
                  <Icon name={o.icon} size={14} />
                  {o.name}
                </motion.button>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-[var(--evara-muted)]">
                Working in{" "}
                <span className="font-serif italic text-[var(--evara-primary)]">{sector.name}</span>?
              </p>
              <Link
                to="/book"
                className="btn-primary"
                data-testid={`sector-cta-${sector.key}`}
              >
                Schedule a Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* --------------- Industries page shell --------------- */
export default function Industries() {
  const [params, setParams] = useSearchParams();
  const paramKey = params.get("sector");

  // Back-compat: allow numeric sector index (0-5)
  let sector = INDUSTRIES.find((s) => s.key === paramKey);
  if (!sector && paramKey !== null) {
    const asIdx = parseInt(paramKey, 10);
    if (Number.isInteger(asIdx) && asIdx >= 0 && asIdx < INDUSTRIES.length) {
      sector = INDUSTRIES[asIdx];
    }
  }
  const focused = Boolean(sector);
  const index = focused ? INDUSTRIES.findIndex((s) => s.key === sector.key) : -1;

  const navigate = useNavigate();
  const goTo = (key) => {
    setParams({ sector: key });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    // navigate ensures back-compat when the sector isn't already in the current URL
    navigate(`/industries?sector=${key}`);
  };

  return (
    <SiteLayout>
      {focused ? (
        <SectorPage sector={sector} index={index} onSelect={goTo} />
      ) : (
        <IndustriesHub onSelect={goTo} />
      )}
    </SiteLayout>
  );
}
